import React, { useState, useMemo } from 'react'
import { api } from '../api'
import { PlatformAdmin } from '../types'

interface Props {
  platformAdmins: PlatformAdmin[]
  currentAdmin: PlatformAdmin | null
  onRefresh: () => void
  createModalOpen?: boolean
  onCloseCreateModal?: () => void
}

export function PlatformAdminsView({
  platformAdmins,
  currentAdmin,
  onRefresh,
  createModalOpen = false,
  onCloseCreateModal,
}: Props) {
  const [search, setSearch] = useState('')
  const [isCreateOpen, setIsCreateOpen] = useState(createModalOpen)
  const [editingAdmin, setEditingAdmin] = useState<PlatformAdmin | null>(null)

  // Form states
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isActive, setIsActive] = useState(true)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const filteredAdmins = useMemo(() => {
    return platformAdmins.filter((a) => {
      const matchSearch =
        (a.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
        a.email.toLowerCase().includes(search.toLowerCase())
      return matchSearch
    })
  }, [platformAdmins, search])

  const handleOpenCreate = () => {
    setFullName('')
    setEmail('')
    setPassword('')
    setIsActive(true)
    setError(null)
    setIsCreateOpen(true)
  }

  const handleOpenEdit = (a: PlatformAdmin) => {
    setEditingAdmin(a)
    setFullName(a.full_name || '')
    setEmail(a.email)
    setPassword('')
    setIsActive(a.is_active)
    setError(null)
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) {
      setError('E-posta adresi zorunludur.')
      return
    }
    if (!editingAdmin && !password) {
      setError('Yeni platform yöneticisi için şifre zorunludur.')
      return
    }
    if (password && password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.')
      return
    }

    if (editingAdmin && editingAdmin.id === currentAdmin?.id && !isActive) {
      if (!window.confirm('Kendi platform admin hesabınızı pasife almak üzeresiniz. Bu işlem sonrası oturumunuz sonlanabilir. Devam etmek istiyor musunuz?')) {
        return
      }
    }

    setLoading(true)
    setError(null)

    try {
      if (editingAdmin) {
        await api.updatePlatformAdmin(editingAdmin.id, {
          email: email.trim(),
          full_name: fullName.trim() || undefined,
          password: password ? password : undefined,
          is_active: isActive,
        })
        setSuccess(`Platform yöneticisi "${email}" başarıyla güncellendi.`)
        setEditingAdmin(null)
      } else {
        await api.createPlatformAdmin({
          email: email.trim(),
          password,
          full_name: fullName.trim() || undefined,
        })
        setSuccess(`Platform yöneticisi "${email}" başarıyla oluşturuldu.`)
        setIsCreateOpen(false)
        if (onCloseCreateModal) onCloseCreateModal()
      }
      onRefresh()
    } catch (err: any) {
      setError(err.message || 'İşlem sırasında bir hata meydana geldi.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="view-title-block">
          <h2>Platform Yöneticileri (PlatformAdmins)</h2>
          <p>
            Tüm tenantlara ve merkezi ERP platformuna sınırsız yetkiyle erişebilen süper yöneticileri yönetin
          </p>
        </div>
        <div className="view-actions">
          <button type="button" className="btn btn-primary" onClick={handleOpenCreate}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            <span>Platform Admin Ekle</span>
          </button>
        </div>
      </div>

      {success && (
        <div className="alert alert-success">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span>{success}</span>
          <button
            type="button"
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}
            onClick={() => setSuccess(null)}
          >
            ✕
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="toolbar-card">
        <div className="search-input-wrapper">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: 'var(--color-text-dim)' }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Yönetici adı veya e-posta ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
              onClick={() => setSearch('')}
            >
              ✕
            </button>
          )}
        </div>

        <div className="filter-group">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onRefresh}
            title="Listeyi Yenile"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            <span>Yenile</span>
          </button>
        </div>
      </div>

      {/* Data Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Ad & Soyad</th>
              <th>E-Posta Adresi</th>
              <th>Yetki Seviyesi</th>
              <th>Durum</th>
              <th>Oluşturulma Tarihi</th>
              <th style={{ textAlign: 'right' }}>İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filteredAdmins.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="empty-state">
                    <p>Kriterlere uygun platform yöneticisi bulunamadı.</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredAdmins.map((a) => {
                const isCurrent = a.id === currentAdmin?.id
                return (
                  <tr key={a.id} style={isCurrent ? { backgroundColor: '#f0fdf4' } : undefined}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 600 }}>{a.full_name || 'İsimsiz Yönetici'}</span>
                        {isCurrent && (
                          <span
                            style={{
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              backgroundColor: '#dcfce7',
                              color: '#15803d',
                              padding: '2px 6px',
                              borderRadius: 4,
                            }}
                          >
                            Mevcut Oturum
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <code>{a.email}</code>
                    </td>
                    <td>
                      <span className="badge badge-admin">PLATFORM SUPERADMIN</span>
                    </td>
                    <td>
                      <span className={`badge badge-${a.is_active ? 'active' : 'inactive'}`}>
                        {a.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.78rem', color: 'var(--color-text-dim)' }}>
                      {a.created_at ? new Date(a.created_at).toLocaleDateString('tr-TR') : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleOpenEdit(a)}
                      >
                        Düzenle
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Platform Admin Modal */}
      {(isCreateOpen || editingAdmin) && (
        <div className="modal-backdrop">
          <div className="modal-dialog">
            <div className="modal-header">
              <h3 className="modal-title">
                {editingAdmin ? 'Platform Yöneticisini Düzenle' : 'Yeni Platform Admin Oluştur'}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => {
                  setIsCreateOpen(false)
                  setEditingAdmin(null)
                  if (onCloseCreateModal) onCloseCreateModal()
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="form-group">
                  <label className="form-label">Ad & Soyad</label>
                  <input
                    type="text"
                    className="form-input"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Örn: Serdar Çoruhlu"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label form-label-required">E-Posta Adresi</label>
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@parel.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label
                    className={`form-label ${!editingAdmin ? 'form-label-required' : ''}`}
                  >
                    {editingAdmin ? 'Şifre Değiştir (Opsiyonel)' : 'Şifre'}
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={
                      editingAdmin
                        ? 'Değiştirmek istemiyorsanız boş bırakın'
                        : 'En az 6 karakter'
                    }
                    required={!editingAdmin}
                    minLength={6}
                  />
                </div>

                <div className="form-group" style={{ marginTop: '4px' }}>
                  <label className="form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                    <span>Hesap aktif (Platform paneline giriş yetkisine sahip)</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsCreateOpen(false)
                    setEditingAdmin(null)
                    if (onCloseCreateModal) onCloseCreateModal()
                  }}
                  disabled={loading}
                >
                  İptal
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Kaydediliyor…' : editingAdmin ? 'Değişiklikleri Kaydet' : 'Yönetici Oluştur'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
